import React, {useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef} from 'react';
import {Box, Typography, Button} from '@material-ui/core';

const HZ_COUNT = 30

/**
 * 0~30Hz 주파수 이퀄라이저
 * 각 Hz별 계수를 드래그로 조절 (0.1 단위)
 * onChange(curve) 콜백으로 30개 배열 전달
 */
const FrequencyEqualizer = forwardRef(({onChange, width = 600, height = 150, savedCurve, defaultValue = 0, label = '주파수 이퀄라이저 (0~30Hz)', min = -5, max = 5, showInfo = false}, ref) => {
    const defaultCurve = new Array(HZ_COUNT).fill(defaultValue)
    const canvasRef = useRef(null)
    const [curve, setCurve] = useState(savedCurve && savedCurve.length === HZ_COUNT ? [...savedCurve] : [...defaultCurve])
    const curveRef = useRef(curve)
    curveRef.current = curve
    const isDragging = useRef(false)
    const [selectedHz, setSelectedHz] = useState(0)

    const fftDataRef = useRef(null)
    const FFT_HISTORY_SIZE = 3
    const fftHistoryRef = useRef({ch1: [], ch2: []})

    const calcAvg = (history) => {
        if (history.length === 0) return null
        const len = history[0].length
        const avg = new Array(len).fill(0)
        for (let h = 0; h < history.length; h++) {
            for (let i = 0; i < len; i++) avg[i] += history[h][i]
        }
        for (let i = 0; i < len; i++) avg[i] /= history.length
        return avg
    }

    useImperativeHandle(ref, () => ({
        getCurve: () => curveRef.current,
        reset: () => {
            const newCurve = [...defaultCurve]
            setCurve(newCurve)
            curveRef.current = newCurve
            if (onChange) onChange(newCurve)
        },
        updateFFT: (ch1, ch2) => {
            if (ch1) {
                fftHistoryRef.current.ch1.push([...ch1])
                if (fftHistoryRef.current.ch1.length > FFT_HISTORY_SIZE) fftHistoryRef.current.ch1.shift()
            }
            if (ch2) {
                fftHistoryRef.current.ch2.push([...ch2])
                if (fftHistoryRef.current.ch2.length > FFT_HISTORY_SIZE) fftHistoryRef.current.ch2.shift()
            }
            fftDataRef.current = {
                ch1: calcAvg(fftHistoryRef.current.ch1),
                ch2: calcAvg(fftHistoryRef.current.ch2)
            }
            draw()
        },
        resetFFTAvg: () => {
            fftHistoryRef.current = {ch1: [], ch2: []}
            fftDataRef.current = null
        }
    }))

    const barWidth = width / HZ_COUNT
    const range = max - min

    // 값 → Y 좌표 변환 (max가 top, min이 bottom)
    const valToY = (val, h) => h - ((val - min) / range) * h
    // Y 좌표 → 값 변환
    const yToVal = (y, h) => (1 - y / h) * range + min

    const draw = useCallback(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        const w = canvas.width
        const h = canvas.height

        ctx.clearRect(0, 0, w, h)

        // 배경
        ctx.fillStyle = 'rgba(0,0,0,0.85)'
        ctx.fillRect(0, 0, w, h)

        // 1 단위 가이드라인
        for (let v = min; v <= max; v = Math.round((v + 1) * 10) / 10) {
            const lineY = valToY(v, h)
            if (v === defaultValue) {
                ctx.strokeStyle = 'rgba(255,255,255,0.5)'
                ctx.setLineDash([])
            } else if (v % 5 === 0) {
                ctx.strokeStyle = 'rgba(255,255,255,0.3)'
                ctx.setLineDash([4, 4])
            } else {
                ctx.strokeStyle = 'rgba(255,255,255,0.1)'
                ctx.setLineDash([2, 4])
            }
            ctx.beginPath()
            ctx.moveTo(0, lineY)
            ctx.lineTo(w, lineY)
            ctx.stroke()
        }
        ctx.setLineDash([])

        // 바 그리기 (defaultValue 기준으로 위/아래)
        const data = curveRef.current
        const baseY = valToY(defaultValue, h)
        for (let i = 0; i < HZ_COUNT; i++) {
            const x = i * barWidth
            const valY = valToY(data[i], h)

            // 바 색상: 기본값 이상=주황~빨강, 이하=파랑~남색
            if (data[i] > defaultValue) {
                const t = (data[i] - defaultValue) / (max - defaultValue || 1)
                ctx.fillStyle = `rgba(${Math.round(200 + 55 * t)}, ${Math.round(180 * (1 - t))}, 50, 0.8)`
            } else {
                const t = (defaultValue - data[i]) / (defaultValue - min || 1)
                ctx.fillStyle = `rgba(50, ${Math.round(150 * (1 - t))}, ${Math.round(200 + 55 * t)}, 0.8)`
            }

            if (data[i] >= defaultValue) {
                ctx.fillRect(x + 1, valY, barWidth - 2, baseY - valY)
            } else {
                ctx.fillRect(x + 1, baseY, barWidth - 2, valY - baseY)
            }
        }

        // 곡선
        ctx.strokeStyle = '#FFC004'
        ctx.lineWidth = 2
        ctx.beginPath()
        for (let i = 0; i < HZ_COUNT; i++) {
            const x = i * barWidth + barWidth / 2
            const y = valToY(data[i], h)
            if (i === 0) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
        }
        ctx.stroke()

        // 포인트
        for (let i = 0; i < HZ_COUNT; i++) {
            const x = i * barWidth + barWidth / 2
            const y = valToY(data[i], h)
            ctx.fillStyle = '#FFC004'
            ctx.beginPath()
            ctx.arc(x, y, 3, 0, Math.PI * 2)
            ctx.fill()
        }

        // 선택된 바 하이라이트
        if (selectedHz !== null && selectedHz >= 0 && selectedHz < HZ_COUNT) {
            const x = selectedHz * barWidth
            ctx.fillStyle = 'rgba(255,192,4,0.15)'
            ctx.fillRect(x, 0, barWidth, h)
            ctx.strokeStyle = '#FFC004'
            ctx.lineWidth = 1
            ctx.strokeRect(x, 0, barWidth, h)
            // 선택 바 값 표시
            ctx.fillStyle = '#FFC004'
            ctx.font = 'bold 14px sans-serif'
            ctx.textAlign = 'center'
            ctx.fillText(`${selectedHz}Hz: ${data[selectedHz]}`, x + barWidth / 2, 12)
        }

        // Hz 라벨 (5Hz 간격)
        ctx.fillStyle = 'rgba(255,255,255,0.6)'
        ctx.font = '13px sans-serif'
        ctx.textAlign = 'center'
        for (let i = 0; i < HZ_COUNT; i += 5) {
            const x = i * barWidth + barWidth / 2
            ctx.fillText(`${i}`, x, h - 2)
        }
        ctx.fillText('30', (HZ_COUNT - 1) * barWidth + barWidth / 2, h - 2)

        // 좌측 값 라벨
        ctx.fillStyle = 'rgba(255,255,255,0.5)'
        ctx.textAlign = 'left'
        ctx.font = '12px sans-serif'
        ctx.fillText(max > 0 ? `+${max}` : `${max}`, 2, 10)
        ctx.fillText(`${defaultValue}`, 2, baseY - 2)
        ctx.fillText(`${min}`, 2, h - 12)

        // FFT 데이터 라인 오버레이
        if (fftDataRef.current) {
            const {ch1, ch2} = fftDataRef.current
            const drawFFTLine = (fft, color) => {
                if (!fft || fft.length === 0) return
                ctx.strokeStyle = color
                ctx.lineWidth = 1.5
                ctx.setLineDash([])
                ctx.beginPath()
                for (let i = 0; i < HZ_COUNT; i++) {
                    const x = i * barWidth + barWidth / 2
                    const val = i < fft.length ? fft[i] : 0
                    const y = valToY(Math.max(min, Math.min(max, val)), h)
                    if (i === 0) ctx.moveTo(x, y)
                    else ctx.lineTo(x, y)
                }
                ctx.stroke()
            }
            drawFFTLine(ch1, 'rgba(0,200,255,0.8)')   // ch1: 파란색
            drawFFTLine(ch2, 'rgba(255,100,100,0.8)')  // ch2: 빨간색
        }
    }, [barWidth, height, width, min, max, defaultValue, range, selectedHz])

    useEffect(() => {
        draw()
    }, [curve, draw])

    const getHzFromEvent = (e) => {
        const canvas = canvasRef.current
        const rect = canvas.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const hz = Math.floor(x / barWidth)
        const raw = yToVal(y, canvas.height)
        const clamped = Math.max(min, Math.min(max, raw))
        const val = Math.round(clamped * 10) / 10  // 0.1 단위 스냅
        return {hz: Math.max(0, Math.min(HZ_COUNT - 1, hz)), val}
    }

    const applyChange = (hz, val) => {
        const newCurve = [...curveRef.current]
        newCurve[hz] = val
        setCurve(newCurve)
        curveRef.current = newCurve
        if (onChange) onChange(newCurve)
    }

    const mouseDownPos = useRef(null)
    const didDrag = useRef(false)

    const handleMouseDown = (e) => {
        isDragging.current = true
        didDrag.current = false
        mouseDownPos.current = {x: e.clientX, y: e.clientY}
        const {hz} = getHzFromEvent(e)
        setSelectedHz(hz)
    }

    const handleMouseMove = (e) => {
        if (!isDragging.current) return
        if (!didDrag.current && mouseDownPos.current) {
            const dx = Math.abs(e.clientX - mouseDownPos.current.x)
            const dy = Math.abs(e.clientY - mouseDownPos.current.y)
            if (dx > 3 || dy > 3) didDrag.current = true
        }
        if (didDrag.current) {
            const {hz, val} = getHzFromEvent(e)
            applyChange(hz, val)
        }
    }

    const handleMouseUp = (e) => {
        isDragging.current = false
        didDrag.current = false
    }

    const adjustSelected = (delta) => {
        if (selectedHz === null) return
        const newCurve = [...curveRef.current]
        const newVal = Math.round((newCurve[selectedHz] + delta) * 10) / 10
        newCurve[selectedHz] = Math.max(min, Math.min(max, newVal))
        setCurve(newCurve)
        curveRef.current = newCurve
        if (onChange) onChange(newCurve)
    }

    return (
        <Box style={{position: 'relative'}}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{marginBottom: 4}}>
                <Typography style={{color: '#fff', fontSize: 16, fontWeight: 'bold'}}>
                    {label}
                </Typography>
                <Box display="flex" alignItems="center" style={{gap: 4}}>
                    {selectedHz !== null && (
                        <>
                            <span style={{color: '#FFC004', fontSize: 14, fontWeight: 'bold'}}>{selectedHz}Hz</span>
                            <Button
                                size="small"
                                variant="outlined"
                                style={{color: '#FFC004', fontSize: 14, minWidth: 'auto', padding: '2px 10px', borderColor: 'rgba(255,192,4,0.5)'}}
                                onClick={() => adjustSelected(0.1)}
                            >
                                +0.1
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                style={{color: '#FFC004', fontSize: 14, minWidth: 'auto', padding: '2px 10px', borderColor: 'rgba(255,192,4,0.5)'}}
                                onClick={() => adjustSelected(-0.1)}
                            >
                                -0.1
                            </Button>
                            <span style={{color: 'rgba(255,255,255,0.3)', margin: '0 2px'}}>|</span>
                        </>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        style={{color: '#fff', fontSize: 14, minWidth: 'auto', padding: '2px 10px', borderColor: 'rgba(255,255,255,0.4)'}}
                        onClick={() => {
                            const newCurve = curveRef.current.map(v => Math.min(max, Math.round((v + 0.1) * 10) / 10))
                            setCurve(newCurve)
                            curveRef.current = newCurve
                            if (onChange) onChange(newCurve)
                        }}
                    >
                        전체 +0.1
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        style={{color: '#fff', fontSize: 14, minWidth: 'auto', padding: '2px 10px', borderColor: 'rgba(255,255,255,0.4)'}}
                        onClick={() => {
                            const newCurve = curveRef.current.map(v => Math.max(min, Math.round((v - 0.1) * 10) / 10))
                            setCurve(newCurve)
                            curveRef.current = newCurve
                            if (onChange) onChange(newCurve)
                        }}
                    >
                        전체 -0.1
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        style={{color: '#fff', fontSize: 14, minWidth: 'auto', padding: '2px 10px', borderColor: 'rgba(255,255,255,0.4)'}}
                        onClick={() => {
                            const newCurve = [...defaultCurve]
                            setCurve(newCurve)
                            curveRef.current = newCurve
                            if (onChange) onChange(newCurve)
                        }}
                    >
                        초기화
                    </Button>
                </Box>
            </Box>
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{borderRadius: 4, cursor: 'crosshair', display: 'block'}}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            />
            {showInfo && selectedHz !== null && (
                <Box display="flex" alignItems="center" style={{marginTop: 6, gap: 8}}>
                    <Button
                        size="small"
                        variant="outlined"
                        style={{color: '#fff', fontSize: 18, minWidth: 36, padding: '2px 0', borderColor: 'rgba(255,255,255,0.4)'}}
                        onClick={() => setSelectedHz(Math.max(0, selectedHz - 1))}
                        disabled={selectedHz <= 0}
                    >
                        ◀
                    </Button>
                    <Typography style={{color: '#FFC004', fontSize: 28, fontWeight: 'bold', lineHeight: 1, minWidth: 80, textAlign: 'center'}}>
                        {selectedHz} Hz
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        style={{color: '#fff', fontSize: 18, minWidth: 36, padding: '2px 0', borderColor: 'rgba(255,255,255,0.4)'}}
                        onClick={() => setSelectedHz(Math.min(HZ_COUNT - 1, selectedHz + 1))}
                        disabled={selectedHz >= HZ_COUNT - 1}
                    >
                        ▶
                    </Button>
                    <Typography style={{color: '#fff', fontSize: 28, fontWeight: 'bold', lineHeight: 1, marginLeft: 8}}>
                        {curve[selectedHz]}
                    </Typography>
                </Box>
            )}
        </Box>
    )
})

export default FrequencyEqualizer
